<template>
    <AiSummaryWidgetWrapper
        :widget-data="props.data"
        :has-content="!!parsed"
        :call-count="callCount"
        :included-call-count="includedCallCount"
        :skipped-call-count="skippedCallCount"
        :analysis-progress="analysisProgress"
        :is-regenerating="isRegenerating"
        :is-exporting="isExporting"
        :is-downloading-md="isDownloadingMd"
        wrapper-class="raw-summary-text py-6 px-8"
        @regenerate="onRegenerateSummary"
        @export-docx="onExportToDocx"
        @download-md="onDownloadMarkdown"
    >
        <h3
            v-if="parsed?.title"
            class="title mb-4"
        >
            {{ parsed.title }}
        </h3>

        <VcMdEditor
            v-if="parsed?.body"
            :model-value="parsed.body"
            readonly
            hide-toolbar
            :min-height="0"
            class="ai-summary-full-text"
        />
    </AiSummaryWidgetWrapper>
</template>

<script setup lang="ts">
//TODO CHECK STYLES AND UI AFTER MIGRATION
import { computed, ref } from 'vue'
import { WidgetExtended } from '@/types/dashboard'
import useNextUpdatedWidgetData from '@/composables/useNextUpdatedWidgetData'
import { AISummaryResponse } from '@/types/dashboard'
import { VcMdEditor } from '@voicenter-team/voicenter-ui-plus'
import AiSummaryWidgetWrapper from '@/components/RedesignComponents/Widgets/Common/AiSummaryWidgetWrapper.vue'
import { useAiSummaryProgress } from '@/composables/useAiSummaryProgress'
import { regenerateAiSummary, exportMarkdownToDocx, downloadMarkdownFile } from '@/helpers/aiSummaryHelpers'

interface ParsedSummary {
    title?: string
    body?: string
}

const props = defineProps<{
    data: WidgetExtended
}>()

const { data: widgetData, run } = useNextUpdatedWidgetData<
    AISummaryResponse,
    typeof props,
    'data'
>(props, 'data')

/* -------------------------------------
 * Loading states
 * ----------------------------------- */
const isRegenerating = ref(false)
const isExporting = ref(false)
const isDownloadingMd = ref(false)

/* -------------------------------------
 * Computed parsing logic - Title + Body specific
 * ----------------------------------- */
const parsed = computed<ParsedSummary | null>(() => {
    const raw = widgetData?.value?.SummaryText
    if (!raw) return null

    try {
        const parsed = JSON.parse(raw)
        return typeof parsed === 'object' && parsed !== null
            ? parsed
            : null
    } catch {
        return null
    }
})

/* -------------------------------------
 * Generate full markdown content (on-demand)
 * ----------------------------------- */
function getFullMarkdownContent (): string {
    if (!parsed.value) return ''

    const parts: string[] = []

    if (parsed.value.title) {
        parts.push(`# ${parsed.value.title}`)
        parts.push('') // Empty line after title
    }

    if (parsed.value.body) {
        parts.push(parsed.value.body)
    }

    return parts.join('\n')
}

/* -------------------------------------
 * Use progress composable
 * ----------------------------------- */
const { callCount, includedCallCount, skippedCallCount, analysisProgress } = useAiSummaryProgress(widgetData)

/* -------------------------------------
 * Action handlers
 * ----------------------------------- */
async function onRegenerateSummary () {
    if (!props.data.WidgetID || isRegenerating.value) return

    isRegenerating.value = true
    try {
        await regenerateAiSummary(props.data.WidgetID, run)
    } catch (error) {
        console.error('Failed to regenerate summary:', error)
    } finally {
        isRegenerating.value = false
    }
}

async function onExportToDocx () {
    const markdownContent = getFullMarkdownContent()
    if (!markdownContent || isExporting.value) return

    isExporting.value = true
    try {
        await exportMarkdownToDocx(markdownContent, props.data)
    } catch (error) {
        console.error('Failed to export to DOCX:', error)
    } finally {
        isExporting.value = false
    }
}

function onDownloadMarkdown () {
    const markdownContent = getFullMarkdownContent()
    if (!markdownContent || isDownloadingMd.value) return

    isDownloadingMd.value = true
    try {
        downloadMarkdownFile(markdownContent, props.data)
    } catch (error) {
        console.error('Failed to download markdown:', error)
    } finally {
        isDownloadingMd.value = false
    }
}
</script>

<style lang="scss" scoped>
.title {
  font-weight: 500;
}
</style>

<style lang="scss">
.ai-summary-full-text {
  .milkdown {
    background: transparent !important;
  }
}
</style>
